import React, { useEffect, useState } from 'react';
import TeamHeader from '../TeamHeader/TeamHeader';
import FilterDropdown from '../TeamPortalUtil/FilterDropdown';
import { FaMagnifyingGlass } from 'react-icons/fa6';

// Toast imports
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Detector data imports
import { useDetectorRelations } from './useDetectorRelations';

// Icons
import { FaFileCsv } from 'react-icons/fa';

type SortOption = 'alphabetical' | 'newest' | 'oldest';

const DetectorReport = () => {
    const { fullDetectors, detectorCodeObj, lastFetched } = useDetectorRelations();

    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilters, setStatusFilters] = useState<string[]>([]);
    const [testFilter, setTestFilter] = useState<'include' | 'exclude'>('include');
    const [regions, setRegions] = useState<string[]>(['All']);
    const [selectedRegions, setSelectedRegions] = useState<string[]>(['All']);

    // Restore sort from localStorage (shared key 'lastSort' with old Report.tsx)
    const [sortOption, setSortOption] = useState<SortOption>(() => {
        const saved = localStorage.getItem('lastSort') as SortOption | null;
        return saved ?? 'newest';
    });

    const [selectedDetectors, setSelectedDetectors] = useState<Set<string>>(new Set());

    // Live ticker so "Last Updated" refreshes every second
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const interval = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, []);

    // Extract unique regions (same logic as DetectorStatus)
    useEffect(() => {
        if (!detectorCodeObj.length) return;
        const unique = Array.from(
            new Set(detectorCodeObj.map(obj => obj.reg).filter(Boolean))
        ).filter(region => region !== 'Test');
        setRegions(['All', ...unique]);
    }, [detectorCodeObj]);

    // Build processed rows (same code/displayCode logic as DetectorStatus)
    const processedDetectors = fullDetectors.map(det => {
        const nowDate = new Date();
        let displayCode = 'T';

        if (det.code) {
            const expiration = det.code.expiration_date
                ? new Date(det.code.expiration_date)
                : null;
            const notExpired = !expiration || expiration > nowDate;
            displayCode = notExpired ? (det.code.code?.trim() || 'U') : 'T';
        }

        const displayName =
            det.code?.name?.trim() ||
            det.code?.code?.trim() ||
            det.monitoring.status.site_name;

        // Location: live GPS if it has a fix, otherwise the registered site lat/lon
        const isValid = (lat: number, lon: number) =>
            Number.isFinite(lat) && Number.isFinite(lon) && !(lat === 0 && lon === 0);

        let location: { lat: number; lon: number; source: 'gps' | 'site' } | null = null;
        const gpsLat = Number(det.monitoring.status?.gps_lat);
        const gpsLon = Number(det.monitoring.status?.gps_long);
        const siteLat = parseFloat(det.code?.lat ?? '');
        const siteLon = parseFloat(det.code?.lon ?? '');

        if (det.monitoring.status?.gps_fix && isValid(gpsLat, gpsLon)) {
            location = { lat: gpsLat, lon: gpsLon, source: 'gps' };
        } else if (isValid(siteLat, siteLon)) {
            location = { lat: siteLat, lon: siteLon, source: 'site' };
        }

        return { ...det, displayCode, displayName, location };
    });

    // ---- Filtering (mirrors DetectorStatus logic) ----
    const filteredDetectors = (() => {
        let base = processedDetectors;

        // Region
        if (!selectedRegions.includes('All')) {
            base = base.filter(det => det.code && selectedRegions.includes(det.code.reg));
        }

        // Search
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            base = base.filter(d => {
                const mac = d.monitoring.mac?.toLowerCase() || '';
                const site = d.monitoring.status?.site_name?.toLowerCase() || '';
                const ip = d.monitoring.ip?.toLowerCase() || '';
                const receiver = d.code?.receiver?.toLowerCase() || '';
                const codeName = d.code?.name?.toLowerCase() || '';
                const code = d.code?.code?.toLowerCase() || '';
                return (
                    mac.includes(term) ||
                    site.includes(term) ||
                    ip.includes(term) ||
                    receiver.includes(term) ||
                    codeName.includes(term) ||
                    code.includes(term)
                );
            });
        }

        // Status
        if (statusFilters.length > 0) {
            base = base.filter(d => {
                const status = d.monitoring.online ? 'Online' : 'Offline';
                return statusFilters.includes(status);
            });
        }

        // Test (exclude "T" detectors)
        if (testFilter === 'exclude') {
            base = base.filter(d => d.displayCode !== 'T');
        }

        return base;
    })();

    // ---- Sorting ----
    const sortedDetectors = (() => {
        const arr = [...filteredDetectors];

        if (sortOption === 'alphabetical') {
            return arr.sort((a, b) =>
                (a.displayName || '').localeCompare(b.displayName || '')
            );
        }

        if (sortOption === 'newest') {
            // Newest = detectors that appear at the END of the monitoring fetch
            return arr.sort((a, b) => {
                const iA = fullDetectors.findIndex(d => d.monitoring.mac === a.monitoring.mac);
                const iB = fullDetectors.findIndex(d => d.monitoring.mac === b.monitoring.mac);
                return iB - iA;
            });
        }

        if (sortOption === 'oldest') {
            return arr.sort((a, b) => {
                const iA = fullDetectors.findIndex(d => d.monitoring.mac === a.monitoring.mac);
                const iB = fullDetectors.findIndex(d => d.monitoring.mac === b.monitoring.mac);
                return iA - iB;
            });
        }

        return arr;
    })();

    const toggleDetectorSelection = (mac: string) => {
        setSelectedDetectors(prev => {
            const next = new Set(prev);
            if (next.has(mac)) next.delete(mac);
            else next.add(mac);
            return next;
        });
    };

    // ---- Helpers ----
    const getStatusStyle = (online: boolean) => ({
        color: online ? '#008000' : '#cc0000',
        fontWeight: 600 as const,
    });

    const getMemoryStyle = (memory: string) => {
        const val = parseFloat(memory);
        if (isNaN(val)) return { color: '#cc0000' };
        if (val >= 85) return { color: '#cc0000' };
        if (val >= 80) return { color: '#d5b60a' };
        return { color: '#008000' };
    };

    // "X seconds/minutes/hours/days ago" — identical logic to DetectorStatus
    const formatTimeAgo = (date: Date | null): string => {
        if (!date) return '—';
        const diff = Math.max(0, Math.floor((now - date.getTime()) / 1000));
        if (diff < 60) return `${diff} second${diff !== 1 ? 's' : ''} ago`;
        if (diff < 3600) {
            const m = Math.floor(diff / 60);
            return `${m} minute${m !== 1 ? 's' : ''} ago`;
        }
        if (diff < 86400) {
            const h = Math.floor(diff / 3600);
            return `${h} hour${h !== 1 ? 's' : ''} ago`;
        }
        const d = Math.floor(diff / 86400);
        return `${d} day${d !== 1 ? 's' : ''} ago`;
    };

    // Last Active column — reuses formatTimeAgo
    const formatLastActive = (lastActive: string): string => {
        if (!lastActive) return 'N/A';
        return formatTimeAgo(new Date(lastActive + 'Z'));
    };

    // Fix 3: always show receiver with "0x" prefix (some entries arrive without it)
    const displayReceiver = (receiver: string | undefined, fallback: string): string => {
        if (!receiver) return fallback || '—';
        return receiver.toLowerCase().startsWith('0x') ? receiver : `0x${receiver}`;
    };

    const exportDetectors = () => {
        const toExport = sortedDetectors.filter(det =>
            selectedDetectors.has(det.monitoring.mac)
        );

        if (toExport.length === 0) {
            toast.warning('No detectors selected');
            return;
        }

        const headers = [
            'Code', 'Site Name', 'MAC', 'Created On',
            'Up Since', 'Last Active', 'Status', 'Memory', 'GPS Fix',
            'Latitude', 'Longitude'
        ];

        const formatDate = (val: any) => {
            if (!val) return '';
            const d = new Date(val);
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}/${m}/${day}`;
        };

        const formatUpSince = (val: any) => {
            if (!val) return '';
            if (typeof val === 'string') return val.replace(/\s*UTC$/, '');
            return new Date(val).toLocaleString();
        };

        const rows = toExport.map(det => [
            det.displayCode,
            det.displayName || '',
            displayReceiver(det.code?.receiver, det.monitoring.mac),
            formatDate(det.code?.created_at),
            formatUpSince(det.monitoring.status?.up_since),
            formatLastActive(det.monitoring.last_active),
            det.monitoring.online ? 'Online' : 'Offline',
            det.monitoring.status?.memory ? `${det.monitoring.status.memory}%` : '0%',
            det.monitoring.status?.gps_fix ? 'Yes' : 'No',
            det.monitoring.status?.gps_lat ?? '',
            det.monitoring.status?.gps_long ?? ''
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(r => r.map(v => `"${v}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', `detectors_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        toast.success(`${rows.length} detector${rows.length !== 1 ? 's' : ''} exported`);
    };

    return (
        <div className='customer-directory-body'>
            <TeamHeader title="Hardware" />
            <ToastContainer />

            <div className="customer-title-and-expand-buttons">
                <h1 className='cutomer-title'>Detector Report</h1>

                {/* Live ticker, same style as DetectorStatus */}
                <div className='detector-last-updated-text'>
                    <p><strong>Last Updated</strong></p>
                    <p>{formatTimeAgo(lastFetched)}</p>
                </div>
            </div>

            {/* Controls */}
            <div className='hardware3-action-buttons'>
                <div className='camera-filters'>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {/* Search */}
                        <div className="customer-search-wrapper">
                            <FaMagnifyingGlass className="search-icon" />
                            <input
                                className="customer-search"
                                type="text"
                                placeholder="Search by site, MAC, IP, or code..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>

                        {/* Filter Dropdown */}
                        <FilterDropdown
                            dropdownClassName="detector-status-dropdown"
                            sideClass="detector-side-drop"
                            initialSelected={{ test: [testFilter] }}
                            resetValue={{ test: ['include'] }}
                            sections={[
                                {
                                    key: 'regions',
                                    title: 'Region',
                                    options: regions.filter(r => r !== 'All'),
                                    type: 'checkbox',
                                },
                                {
                                    key: 'status',
                                    title: 'Status',
                                    options: ['Online', 'Offline'],
                                    type: 'checkbox',
                                },
                                {
                                    key: 'test',
                                    title: 'Test',
                                    options: ['include', 'exclude'],
                                    type: 'radio',
                                    formatLabel: (option: string) =>
                                        option.charAt(0).toUpperCase() + option.slice(1),
                                },
                            ]}
                            onChange={(filters: Record<string, string[]>) => {
                                setSelectedRegions(
                                    filters.regions && filters.regions.length > 0
                                        ? filters.regions
                                        : ['All']
                                );
                                setStatusFilters(filters.status || []);
                                setTestFilter(
                                    (filters.test?.[0] || 'include') as 'include' | 'exclude'
                                );
                            }}
                        />
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>

                    {/* Sort */}
                    <select
                        className="hardware3-open-popup-button hardware3-filter-select camera-select"
                        value={sortOption}
                        onChange={e => {
                            const val = e.target.value as SortOption;
                            setSortOption(val);
                            localStorage.setItem('lastSort', val);
                        }}
                    >
                        <option value="newest">Newest</option>
                        <option value="oldest">Oldest</option>
                        <option value="alphabetical">A → Z</option>
                    </select>

                    {/* Export Button */}
                    <button
                        className='inv-order-btn inv-order-btn order-btn2'
                        onClick={exportDetectors}
                    >
                        <FaFileCsv className='inv-order-btn-bag-svg' />
                        <span> Export</span>
                    </button>

                </div>

            </div>

            {/* Table */}
            <div className="hardware3-table-wrapper camera-table-wrapper detector-report-table-wrapper">
                <table className="hardware3-table toa-efficiency-table detector-report-table">
                    <thead>
                        <tr>
                            <th>
                                <div className="toa-efficiency-cell">
                                    <input
                                        type="checkbox"
                                        checked={
                                            sortedDetectors.length > 0 &&
                                            sortedDetectors.every(det =>
                                                selectedDetectors.has(det.monitoring.mac)
                                            )
                                        }
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedDetectors(
                                                    new Set(sortedDetectors.map(det => det.monitoring.mac))
                                                );
                                            } else {
                                                setSelectedDetectors(new Set());
                                            }
                                        }}
                                    />
                                    <span>Code</span>
                                </div>
                            </th>
                            <th>Site Name</th>
                            <th>MAC</th>
                            <th>Created on (UTC)</th>
                            <th>Up Since (UTC)</th>
                            <th>Last Active</th>
                            <th>Status</th>
                            <th>Memory</th>
                            <th>GPS Fix</th>
                            <th>Location</th>
                        </tr>
                    </thead>

                    <tbody>
                        {sortedDetectors.length > 0 ? (
                            sortedDetectors.map(det => (
                                <tr key={det.monitoring.mac}>
                                    {/* Code */}
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <div className="toa-efficiency-cell">
                                            <input
                                                type="checkbox"
                                                checked={selectedDetectors.has(det.monitoring.mac)}
                                                onChange={() => toggleDetectorSelection(det.monitoring.mac)}
                                            />
                                            <span>{det.displayCode}</span>
                                        </div>
                                    </td>

                                    {/* Name */}
                                    <td>{det.displayName || '—'}</td>

                                    {/* MAC — receiver with guaranteed 0x prefix */}
                                    <td>{displayReceiver(det.code?.receiver, det.monitoring.mac)}</td>

                                    {/* Created at*/}
                                    <td>
                                        {det.code?.created_at
                                            ? (() => {
                                                const d = new Date(det.code.created_at);
                                                const year = d.getFullYear();
                                                const month = String(d.getMonth() + 1).padStart(2, '0');
                                                const day = String(d.getDate()).padStart(2, '0');
                                                return `${year}/${month}/${day}`;
                                            })()
                                            : '—'}
                                    </td>

                                    {/* Up Since */}
                                    <td>
                                        {det.monitoring.status?.up_since
                                            ? typeof det.monitoring.status.up_since === 'string'
                                                ? det.monitoring.status.up_since.replace(/\s*UTC$/, '')
                                                : new Date(det.monitoring.status.up_since).toLocaleString()
                                            : '—'}
                                    </td>

                                    {/* Last Active as "X ago" */}
                                    <td>{formatLastActive(det.monitoring.last_active)}</td>

                                    {/* Status */}
                                    <td>
                                        <span style={getStatusStyle(det.monitoring.online)}>
                                            {det.monitoring.online ? 'Online' : 'Offline'}
                                        </span>
                                    </td>

                                    {/* Memory */}
                                    <td>
                                        <span style={getMemoryStyle(det.monitoring.status?.memory)}>
                                            {det.monitoring.status?.memory
                                                ? `${det.monitoring.status.memory}%`
                                                : '0%'}
                                        </span>
                                    </td>

                                    {/* GPS Fix */}
                                    <td>
                                        <span style={{ color: det.monitoring.status?.gps_fix ? '#008000' : '#cc0000' }}>
                                            {det.monitoring.status?.gps_fix ? 'Yes' : 'No'}
                                        </span>
                                    </td>

                                    {/* Location */}
                                    <td className="detector-location-cell">
                                        {det.location ? (
                                            <a
                                                href={`https://www.google.com/maps?q=${det.location.lat},${det.location.lon}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title={det.location.source === 'gps' ? 'Live GPS position' : 'Registered site location'}
                                            >
                                                <span>{det.location.lat.toFixed(5)},</span>{' '}
                                                <span>{det.location.lon.toFixed(5)}</span>
                                            </a>
                                        ) : '—'}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={9} style={{ textAlign: 'center', padding: '2rem' }}>
                                    No detectors found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default DetectorReport;